import {
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useState,
  useRef,
} from "react";
import { Heading } from "@/components/ui/Heading";
import { DonationAmountProps } from "./DonationAmountProps";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import { RadioCard, RadioGroup } from "@/components/ui/Radio";
import classes from "./DonationAmount.module.scss";
import { useDonationFormStore, DonationType } from "@/stores";
import { Text } from "@/components/ui/Text";
import { formatCurrency, getCurrencySymbol } from "@/utils/formatters";
import type { AmountConfig } from "@/types/components/AmountComponent";
import { useTranslation } from "@/hooks/useTranslation";
import { camelCaseKeys } from "@/utils/camel-case-keys";
import { InputCurrency } from "@/components/ui/InputCurrency";
import { useAppConfig } from "@/hooks/useAppConfig";
import { focusAndScrollIntoView } from "@/utils/dom";

const CUSTOM_AMOUNT = "custom" as const;

type AmountConfigFormatted = {
  monthlyDonationEnabled: AmountConfig["monthly-donation-enabled"];
  monthlyDonationAmounts: AmountConfig["monthly-donation-amounts"];
  monthlyDonationLabel: AmountConfig["monthly-donation-label"];
  monthlyDonationOther: AmountConfig["monthly-donation-other"];
  monthlyDonationOtherAmountDescription: AmountConfig["monthly-donation-other-amount-description"];
  oneOffDonationEnabled: AmountConfig["one-off-donation-enabled"];
  oneOffDonationLabel: AmountConfig["one-off-donation-label"];
  oneOffDonationOther: AmountConfig["one-off-donation-other"];
  oneOffDonationOtherAmountDescription: AmountConfig["one-off-donation-other-amount-description"];
  oneOffDonationAmounts: AmountConfig["one-off-donation-amounts"];
  amountIntro: AmountConfig["amount-intro"];
  oneOffDonationAmountMin: AmountConfig["one-off-donation-amount-min"];
  monthlyDonationAmountMin: AmountConfig["monthly-donation-amount-min"];
  recurringDonationEnabled: AmountConfig["recurring-donation-enabled"];
  recurringDonationLabel: AmountConfig["recurring-donation-label"];
  recurringDonationAmounts: AmountConfig["recurring-donation-amounts"];
  recurringDonationOther: AmountConfig["recurring-donation-other"];
  recurringDonationOtherAmountDescription: AmountConfig["recurring-donation-other-amount-description"];
  recurringDonationAmountMin: NonNullable<
    AmountConfig["recurring-donation-amount-min"]
  >;
};

export function DonationAmount(props: DonationAmountProps) {
  const { config, className, ref } = props;

  const {
    monthlyDonationEnabled,
    monthlyDonationLabel,
    monthlyDonationOther,
    monthlyDonationAmounts,
    oneOffDonationAmounts,
    amountIntro,
    monthlyDonationOtherAmountDescription,
    oneOffDonationEnabled,
    oneOffDonationLabel,
    oneOffDonationOther,
    oneOffDonationOtherAmountDescription,
    oneOffDonationAmountMin,
    monthlyDonationAmountMin,
    recurringDonationEnabled,
    recurringDonationLabel,
    recurringDonationAmounts,
    recurringDonationOther,
    recurringDonationOtherAmountDescription,
    recurringDonationAmountMin,
  } = camelCaseKeys(config) as unknown as AmountConfigFormatted;

  const { amountTitle } = useAppConfig();

  const [monthlyCustomAmmountError, setMonthlyCustomAmmountError] = useState<
    string | null
  >(null);
  const [oneOffCustomAmmountError, setOneOffCustomAmmountError] = useState<
    string | null
  >(null);
  const [recurringCustomAmmountError, setRecurringCustomAmmountError] =
    useState<string | null>(null);

  const {
    donationType,
    setDonationType,
    donationAmount,
    setDonationAmount,
    setHasCustomAmount,
    hasCustomAmount,
    setEditingCustomAmount,
  } = useDonationFormStore();

  const t = useTranslation("amount");
  const customAmountTitle = t("custom-amount-title") as string;
  const customAmountInputLabel = t("custom-amount-input-label", {
    label: customAmountTitle,
    currency: getCurrencySymbol(),
  }) as string;
  const inputRef = useRef<HTMLInputElement>(null);

  const [customAmount, setCustomAmount] = useState<string>(() => {
    const hasValidCustomAmount =
      hasCustomAmount &&
      donationAmount !== null &&
      donationAmount !== undefined;

    return hasValidCustomAmount ? donationAmount.toString() : "";
  });

  const amountRules = {
    [DonationType.OneOff]: {
      enabled: oneOffDonationEnabled,
      min: oneOffDonationAmountMin,
      error: oneOffCustomAmmountError,
      setError: setOneOffCustomAmmountError,
    },
    [DonationType.Monthly]: {
      enabled: monthlyDonationEnabled,
      min: monthlyDonationAmountMin,
      error: monthlyCustomAmmountError,
      setError: setMonthlyCustomAmmountError,
    },
    [DonationType.Recurring]: {
      enabled: !!recurringDonationEnabled,
      min: recurringDonationAmountMin,
      error: recurringCustomAmmountError,
      setError: setRecurringCustomAmmountError,
    },
  };

  const activeRule = donationType ? amountRules[donationType] : undefined;

  useImperativeHandle(ref, () => ({
    validate: async () => {
      const isBelowMinimum =
        !donationAmount || donationAmount < (activeRule?.min ?? 0);

      if (!activeRule?.enabled || !isBelowMinimum) {
        return {
          isValid: true,
        };
      }

      activeRule.setError(
        t("errors.amount-minimum", {
          minimum: formatCurrency(activeRule.min),
        }) as string,
      );

      focusAndScrollIntoView(inputRef.current);

      return {
        isValid: false,
      };
    },
  }));

  // The recurring donation is a distinct tab only for migrated forms: recurring
  // enabled, monthly disabled, and its own config present. A form with monthly
  // still enabled is the #984 legacy model (a monthly donation submitted as
  // recurring) and keeps its monthly tab; a bare `recurring-donation-enabled`
  // flag with no recurring config is that same legacy model, never a tab.
  const showRecurringType =
    !!recurringDonationEnabled &&
    !monthlyDonationEnabled &&
    !!recurringDonationLabel &&
    (recurringDonationAmounts?.length ?? 0) > 0;

  const startEditingCustomAmount = () => setEditingCustomAmount(true);
  const stopEditingCustomAmount = () => setEditingCustomAmount(false);

  // Not every browser fires blur on an input that unmounts while focused.
  useEffect(
    () => () => setEditingCustomAmount(false),
    [setEditingCustomAmount],
  );

  useLayoutEffect(() => {
    const enabledTypes = [
      oneOffDonationEnabled && DonationType.OneOff,
      monthlyDonationEnabled && DonationType.Monthly,
      showRecurringType && DonationType.Recurring,
    ].filter(Boolean) as DonationType[];

    if (enabledTypes.length === 1) {
      setDonationType(enabledTypes[0]);
    }
  }, [
    oneOffDonationEnabled,
    monthlyDonationEnabled,
    showRecurringType,
    setDonationType,
  ]);

  const resetDonation = () => {
    setDonationAmount(null);
    setCustomAmount("");
    setHasCustomAmount(false);
  };

  const handleChangeTab = (tabName: string) => {
    resetDonation();

    setDonationType(tabName as DonationType);
  };

  const clearErrors = () => {
    setMonthlyCustomAmmountError(null);
    setOneOffCustomAmmountError(null);
    setRecurringCustomAmmountError(null);
  };
  const handleChangeAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    clearErrors();

    if (value === CUSTOM_AMOUNT) {
      setHasCustomAmount(true);
      setDonationAmount(null);
    } else {
      setHasCustomAmount(false);
      setCustomAmount("");
      setDonationAmount(Number(e.target.value));
    }
  };

  const handleChangeCustomAmount = (value: string) => {
    clearErrors();
    setCustomAmount(value);
    const customAmountParsed = Number.parseFloat(value);

    if (!Number.isNaN(customAmountParsed) && customAmountParsed >= 1) {
      setDonationAmount(customAmountParsed);
    } else {
      setDonationAmount(null);
    }
  };

  const amount = hasCustomAmount ? CUSTOM_AMOUNT : (donationAmount ?? "");

  const hasValidCustomAmount =
    !!activeRule &&
    customAmount !== "" &&
    !activeRule.error &&
    Number.parseFloat(customAmount) >= activeRule.min;

  const enabledTypesCount = [
    oneOffDonationEnabled,
    monthlyDonationEnabled,
    showRecurringType,
  ].filter(Boolean).length;
  const hasMultipleTypes = enabledTypesCount > 1;

  return (
    <div className={className}>
      <div className={classes.header}>
        <Heading as="h1" size="2xl" gutterBottom>
          {amountTitle}
        </Heading>
        <div className={classes.content}>{t(amountIntro.i18n)}</div>
      </div>

      <Tabs value={donationType ?? ""} onValueChange={handleChangeTab}>
        {hasMultipleTypes && (
          <TabsList>
            {oneOffDonationEnabled && (
              <TabsTrigger value={DonationType.OneOff}>
                {t(oneOffDonationLabel.i18n)}
              </TabsTrigger>
            )}

            {monthlyDonationEnabled && (
              <TabsTrigger value={DonationType.Monthly}>
                {t(monthlyDonationLabel.i18n)}
              </TabsTrigger>
            )}

            {showRecurringType && (
              <TabsTrigger value={DonationType.Recurring}>
                {t(recurringDonationLabel?.i18n ?? "")}
              </TabsTrigger>
            )}
          </TabsList>
        )}

        <TabsContent value={DonationType.OneOff}>
          <RadioGroup
            direction="row"
            name="one-off-donation-amount"
            value={amount}
            onChange={handleChangeAmount}
          >
            {oneOffDonationAmounts.map((amount) => (
              <RadioCard
                key={amount.amount}
                title={formatCurrency(amount.amount)}
                description={t(amount.description.i18n)}
                value={Number(amount.amount)}
                image={amount.image?.src}
                recommended={amount.recommended}
                recommendedText={t("recommended-amount") as string}
              />
            ))}

            {oneOffDonationOther && (
              <RadioCard
                disableSelectedStyle={!hasValidCustomAmount}
                value={CUSTOM_AMOUNT}
                ariaLabel={customAmountTitle}
              >
                <div className={classes.customAmount}>
                  <div>
                    <Heading size="lg" className={classes.spaceBottom}>
                      {customAmountTitle}
                    </Heading>
                    <Text>{t(oneOffDonationOtherAmountDescription.i18n)}</Text>
                  </div>

                  <div className={classes.customAmountInput}>
                    <InputCurrency
                      aria-label={customAmountInputLabel}
                      ref={inputRef}
                      min={oneOffDonationAmountMin}
                      value={customAmount}
                      onChange={handleChangeCustomAmount}
                      onFocus={startEditingCustomAmount}
                      onBlur={stopEditingCustomAmount}
                      placeholder="0.00"
                      hasError={oneOffCustomAmmountError !== null}
                    />

                    {oneOffCustomAmmountError ? (
                      <Text
                        size="sm"
                        className={classes.error}
                        aria-live="polite"
                      >
                        {oneOffCustomAmmountError}
                      </Text>
                    ) : (
                      <Text size="sm">
                        {formatCurrency(oneOffDonationAmountMin)} minimum
                      </Text>
                    )}
                  </div>
                </div>
              </RadioCard>
            )}
          </RadioGroup>
        </TabsContent>

        <TabsContent value={DonationType.Monthly}>
          <RadioGroup
            name="monthly-donation-amount"
            value={amount}
            onChange={handleChangeAmount}
            direction="row"
          >
            {monthlyDonationAmounts.map((amount) => (
              <RadioCard
                key={amount.amount}
                title={formatCurrency(amount.amount)}
                description={t(amount.description.i18n)}
                value={Number(amount.amount)}
                image={amount.image?.src}
                recommended={amount.recommended}
                recommendedText={t("recommended-amount") as string}
              />
            ))}

            {monthlyDonationOther && (
              <RadioCard
                disableSelectedStyle={!hasValidCustomAmount}
                value={CUSTOM_AMOUNT}
                ariaLabel={customAmountTitle}
              >
                <div className={classes.customAmount}>
                  <div>
                    <Heading size="lg" className={classes.spaceBottom}>
                      {customAmountTitle}
                    </Heading>
                    <Text>{t(monthlyDonationOtherAmountDescription.i18n)}</Text>
                  </div>

                  <div className={classes.customAmountInput}>
                    <InputCurrency
                      aria-label={customAmountInputLabel}
                      ref={inputRef}
                      min={monthlyDonationAmountMin}
                      value={customAmount}
                      onChange={handleChangeCustomAmount}
                      onFocus={startEditingCustomAmount}
                      onBlur={stopEditingCustomAmount}
                      placeholder="0.00"
                      hasError={monthlyCustomAmmountError !== null}
                    />

                    {monthlyCustomAmmountError ? (
                      <Text
                        size="sm"
                        className={classes.error}
                        aria-live="polite"
                      >
                        {monthlyCustomAmmountError}
                      </Text>
                    ) : (
                      <Text size="sm">
                        {formatCurrency(monthlyDonationAmountMin)} minimum
                      </Text>
                    )}
                  </div>
                </div>
              </RadioCard>
            )}
          </RadioGroup>
        </TabsContent>

        <TabsContent value={DonationType.Recurring}>
          <RadioGroup
            name="recurring-donation-amount"
            value={amount}
            onChange={handleChangeAmount}
            direction="row"
          >
            {(recurringDonationAmounts ?? []).map((amount) => (
              <RadioCard
                key={amount.amount}
                title={formatCurrency(amount.amount)}
                description={t(amount.description.i18n)}
                value={Number(amount.amount)}
                image={amount.image?.src}
                recommended={amount.recommended}
                recommendedText={t("recommended-amount") as string}
              />
            ))}

            {recurringDonationOther && (
              <RadioCard
                disableSelectedStyle={!hasValidCustomAmount}
                value={CUSTOM_AMOUNT}
                ariaLabel={customAmountTitle}
              >
                <div className={classes.customAmount}>
                  <div>
                    <Heading size="lg" className={classes.spaceBottom}>
                      {customAmountTitle}
                    </Heading>
                    <Text>
                      {t(recurringDonationOtherAmountDescription?.i18n ?? "")}
                    </Text>
                  </div>

                  <div className={classes.customAmountInput}>
                    <InputCurrency
                      aria-label={customAmountInputLabel}
                      ref={inputRef}
                      min={recurringDonationAmountMin}
                      value={customAmount}
                      onChange={handleChangeCustomAmount}
                      onFocus={startEditingCustomAmount}
                      onBlur={stopEditingCustomAmount}
                      placeholder="0.00"
                      hasError={recurringCustomAmmountError !== null}
                    />

                    {recurringCustomAmmountError ? (
                      <Text
                        size="sm"
                        className={classes.error}
                        aria-live="polite"
                      >
                        {recurringCustomAmmountError}
                      </Text>
                    ) : (
                      <Text size="sm">
                        {formatCurrency(recurringDonationAmountMin)} minimum
                      </Text>
                    )}
                  </div>
                </div>
              </RadioCard>
            )}
          </RadioGroup>
        </TabsContent>
      </Tabs>
    </div>
  );
}
